/**
 * BrightDocs Application Router
 *
 * Defines all top-level routes:
 * - /login           Public login page
 * - /admin/*         Platform admin routes (requires platform_admin role)
 * - /portal/*        Tenant portal routes (any authenticated tenant user)
 * - /                Redirects based on user role
 */

import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { useAuth } from './auth/AuthProvider';
import RequireAuth from './auth/RequireAuth';
import LoginPage from './auth/LoginPage';

// ---------------------------------------------------------------------------
// Admin pages (lazy-loaded)
// ---------------------------------------------------------------------------
const AdminLayout = lazy(() => import('./admin/AdminLayout'));
const DashboardPage = lazy(() => import('./admin/DashboardPage'));
const TenantsPage = lazy(() => import('./admin/TenantsPage'));
const TenantDetailPage = lazy(() => import('./admin/TenantDetailPage'));
const UsagePage = lazy(() => import('./admin/UsagePage'));
const UsersPage = lazy(() => import('./admin/UsersPage'));
const ConfigPage = lazy(() => import('./admin/ConfigPage'));
const CatalogPage = lazy(() => import('./admin/CatalogPage'));
const OperationsPage = lazy(() => import('./admin/OperationsPage'));
const SchemaDetailPage = lazy(() => import('./admin/SchemaDetailPage'));

// ---------------------------------------------------------------------------
// Tenant Portal pages (lazy-loaded)
// ---------------------------------------------------------------------------
const PortalLayout = lazy(() => import('./portal/PortalLayout'));
const PortalDashboard = lazy(() => import('./portal/DashboardPage'));
const PlaygroundPage = lazy(() => import('./portal/PlaygroundPage'));
const JobHistoryPage = lazy(() => import('./portal/JobHistoryPage'));
const JobDetailPage = lazy(() => import('./portal/JobDetailPage'));
const AutomationsPage = lazy(() => import('./portal/AutomationsPage'));
const LibraryPage = lazy(() => import('./portal/LibraryPage'));
const PortalSchemaDetailPage = lazy(() => import('./portal/SchemaDetailPage'));
const PortalUsagePage = lazy(() => import('./portal/UsagePage'));
const SettingsShell = lazy(() => import('./portal/SettingsShell'));
const SettingsPage = lazy(() => import('./portal/SettingsPage'));
const MembersPage = lazy(() => import('./portal/MembersPage'));

/** Suspense fallback shown while lazy chunks load. */
function PageLoader() {
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      role="status"
      aria-label="Loading page"
    >
      <CircularProgress />
    </Box>
  );
}

/** Root redirect: sends admins to /admin/overview, others to /portal/home. */
function RootRedirect() {
  const { isAdmin } = useAuth();
  return <Navigate to={isAdmin ? '/admin/overview' : '/portal/home'} replace />;
}

/**
 * Redirect from a legacy admin path to its new destination, preserving any
 * search params (e.g. /admin/jobs?tenant_id=…  →  /admin/operations?tenant_id=…).
 * An optional `tab` is merged in for destinations that live behind a tab.
 */
function LegacyAdminRedirect({ to, tab }: { to: string; tab?: string }) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  if (tab) params.set('tab', tab);
  const search = params.toString();
  return <Navigate to={`${to}${search ? `?${search}` : ''}`} replace />;
}

/** Legacy /admin/schemas/:id → /admin/catalog/schemas/:id (keeps search params). */
function LegacySchemaRedirect() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  return <Navigate to={`/admin/catalog/schemas/${id}${location.search}`} replace />;
}

/** Legacy /portal/history/:jobId → /portal/documents/:jobId (keeps search params). */
function LegacyPortalJobRedirect() {
  const { jobId } = useParams<{ jobId: string }>();
  const location = useLocation();
  return <Navigate to={`/portal/documents/${jobId}${location.search}`} replace />;
}

/** Legacy /portal/schemas/:id → /portal/library/schemas/:id (keeps search params). */
function LegacyPortalSchemaRedirect() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  return <Navigate to={`/portal/library/schemas/${id}${location.search}`} replace />;
}

export default function App() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public */}
        <Route path="/login" element={<LoginPage />} />

        {/* Admin routes */}
        <Route
          path="/admin/*"
          element={
            <RequireAuth role="platform_admin">
              <AdminLayout />
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="overview" replace />} />
          <Route path="overview" element={<DashboardPage />} />
          <Route path="tenants" element={<TenantsPage />} />
          <Route path="tenants/:id" element={<TenantDetailPage />} />
          <Route path="people" element={<UsersPage />} />
          <Route path="platform" element={<ConfigPage />} />
          <Route path="catalog" element={<CatalogPage />} />
          <Route path="catalog/schemas/:id" element={<SchemaDetailPage />} />
          <Route path="operations" element={<OperationsPage />} />
          <Route path="analytics" element={<UsagePage />} />

          {/* Legacy path redirects (pre top-nav IA, 2026-08-24) */}
          <Route path="dashboard" element={<LegacyAdminRedirect to="/admin/overview" />} />
          <Route path="users" element={<LegacyAdminRedirect to="/admin/people" />} />
          <Route path="config" element={<LegacyAdminRedirect to="/admin/platform" />} />
          <Route path="doc-types" element={<LegacyAdminRedirect to="/admin/catalog" />} />
          <Route path="schemas" element={<LegacyAdminRedirect to="/admin/catalog" tab="templates" />} />
          <Route path="schemas/:id" element={<LegacySchemaRedirect />} />
          <Route path="jobs" element={<LegacyAdminRedirect to="/admin/operations" />} />
          <Route path="storage" element={<LegacyAdminRedirect to="/admin/operations" tab="storage" />} />
          <Route path="audit-log" element={<LegacyAdminRedirect to="/admin/operations" tab="audit" />} />
          <Route path="usage" element={<LegacyAdminRedirect to="/admin/analytics" />} />
        </Route>

        {/* Tenant Portal routes */}
        <Route
          path="/portal/*"
          element={
            <RequireAuth>
              <PortalLayout />
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="home" replace />} />
          <Route path="home" element={<PortalDashboard />} />
          <Route path="studio" element={<PlaygroundPage />} />
          <Route path="documents" element={<JobHistoryPage />} />
          <Route path="documents/:jobId" element={<JobDetailPage />} />
          <Route path="automations" element={<AutomationsPage />} />
          <Route path="library" element={<LibraryPage />} />
          <Route path="library/schemas/:id" element={<PortalSchemaDetailPage />} />
          <Route path="insights" element={<PortalUsagePage />} />
          <Route path="settings" element={<SettingsShell />}>
            <Route index element={<SettingsPage />} />
            <Route path="team" element={<MembersPage />} />
          </Route>

          {/* Legacy path redirects (pre top-nav IA, 2026-08-24) */}
          <Route path="dashboard" element={<Navigate to="/portal/home" replace />} />
          <Route path="playground" element={<Navigate to="/portal/studio" replace />} />
          <Route path="history" element={<Navigate to="/portal/documents" replace />} />
          <Route path="history/:jobId" element={<LegacyPortalJobRedirect />} />
          <Route path="batches" element={<Navigate to="/portal/automations" replace />} />
          <Route path="buckets" element={<Navigate to="/portal/automations?tab=buckets" replace />} />
          <Route path="schemas" element={<Navigate to="/portal/library" replace />} />
          <Route path="schemas/:id" element={<LegacyPortalSchemaRedirect />} />
          <Route path="processors" element={<Navigate to="/portal/library?tab=parsers" replace />} />
          <Route path="usage" element={<Navigate to="/portal/insights" replace />} />
          <Route path="members" element={<Navigate to="/portal/settings/team" replace />} />
        </Route>

        {/* Root redirect */}
        <Route path="/" element={<RootRedirect />} />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
